/**
 * Textblöcke einer Mail: die kleinsten Stellen, die man als Ganzes bearbeitet
 * und kommentiert — eine Zelle, ein Absatz, eine Überschrift.
 *
 * Ein Textblock ist ein Element unter <body>, das
 *  (a) Text enthält (Text in script/style/noscript/template/title … zählt nicht),
 *  (b) kein Element aus STRUKTUR enthält und
 *  (c) maximal ist (sein Elternelement erfüllt (a) und (b) nicht).
 * Schlüssel: Tag + Nummer unter allen Elementen dieses Tags (wie
 * getElementsByTagName). Dieselben Regeln gelten im Quelltext (quelltext.ts),
 * damit ein Vorschlag aus dem Browser im gespeicherten HTML wiedergefunden wird.
 *
 * Text direkt in <body> (ohne umschließendes Element) gehört zu keinem Block.
 * Elemente aus der Anzeige (del.za-weg, ins.za-neu, mark.za-zitat) zählen nicht mit.
 */
import { bereinigen } from './anwenden';
import { domBlockText, domTextKnoten, normalisieren, OHNE_TEXT, STRUKTUR } from './quelltext';

export { STRUKTUR, OHNE_TEXT, normalisieren };
export { domBlockText as blockText, domTextKnoten as textKnoten };

export interface BlockSchluessel { tag: string; nr: number }

/**
 * Ein ganzer Abschnitt (Absatz, Kasten, Zeile …) soll samt Gestaltung aus der
 * Mail verschwinden. `block` zeigt dann auf diesen Abschnitt, `vorher_text`
 * ist sein ganzer Text, `nachher_text` leer.
 */
export interface Entfernung {
  /** Wie der Abschnitt heißt (Absatz, Kasten, Zeile …) — nur zur Anzeige. */
  art: string;
  /** Bilder (src) im Abschnitt in Reihenfolge; gehören wie der Text zum Wiederfinden. */
  bilder: string[];
}

/** Ein Änderungsvorschlag für einen Textblock (gespeichert in Mail_feedback.aenderungen). */
export interface Aenderung {
  block: BlockSchluessel;
  /** Normalisierter Text (Leerraum inkl. NBSP zu ' ', getrimmt). */
  vorher_text: string;
  nachher_text: string;
  /** Daten aller Textknoten im Block (Dokumentreihenfolge), vor der Änderung. */
  vorher_knoten: string[];
  /** Gleiche Struktur → neue Textknoten-Daten; sonst null. */
  nachher_knoten: string[] | null;
  /** Nur wenn die Struktur geändert wurde: bereinigtes innerHTML des Blocks. */
  nachher_html: string | null;
  /** Gesetzt: statt den Text zu ändern, wird der ganze Abschnitt entfernt (vorher_knoten / nachher_knoten dann leer). */
  entfernen?: Entfernung | null;
  ergebnis?: 'offen' | 'uebernommen' | 'abgelehnt';
  /** Nur beim Lesen: Stelle im gespeicherten Feld, wenn davor ungültige Stellen weggefallen sind (für mail_feedback_entscheiden). */
  pos?: number;
}

export interface TextBlock {
  el: HTMLElement;
  schluessel: BlockSchluessel;
  text: string;
  knoten: Text[];
}

/** Anker eines Kommentars ({tag, nr, text}); text höchstens ANKER_TEXT_MAX Zeichen. */
export interface Anker extends BlockSchluessel { text: string }
export const ANKER_TEXT_MAX = 300;

const UI_KLASSE = /(^|\s)za-(weg|neu|zitat)(\s|$)/;

export function istUi(e: Element): boolean {
  const c = e.getAttribute('class');
  return !!c && UI_KLASSE.test(c);
}

/** Bits: 1 = Text, 2 = STRUKTUR-Element darunter. Kandidaten (nur Text) landen in `kandidat`. */
function pruefen(el: Element, kandidat: Set<Element>): number {
  let bits = 0;
  for (let k = el.firstChild; k; k = k.nextSibling) {
    if (k.nodeType === 3) {
      if (/\S/.test((k as Text).data)) bits |= 1;
    } else if (k.nodeType === 1) {
      const name = (k as Element).localName;
      if (OHNE_TEXT.has(name)) continue;
      bits |= pruefen(k as Element, kandidat);
      if (STRUKTUR.has(name)) bits |= 2;
    }
  }
  if (bits === 1) kandidat.add(el);
  return bits;
}

function sammeln(el: Element, kandidat: Set<Element>, out: Element[]): void {
  for (let k = el.firstElementChild; k; k = k.nextElementSibling) {
    if (OHNE_TEXT.has(k.localName)) continue;
    if (kandidat.has(k)) out.push(k);
    else sammeln(k, kandidat, out);
  }
}

function nummern(wurzel: Document | Element): Map<Element, number> {
  const zaehler = new Map<string, number>();
  const nr = new Map<Element, number>();
  for (const e of Array.from(wurzel.getElementsByTagName('*'))) {
    if (istUi(e)) continue;
    const z = zaehler.get(e.localName) ?? 0;
    nr.set(e, z);
    zaehler.set(e.localName, z + 1);
  }
  return nr;
}

function startVon(wurzel: Document | Element): Element | null {
  return wurzel.nodeType === 9 ? (wurzel as Document).body : (wurzel as Element);
}

/**
 * Alle Textblöcke unter <body> (bzw. unter `wurzel`, ohne `wurzel` selbst) in
 * Dokumentreihenfolge. Die Nummer zählt im Dokument bzw. in `wurzel`.
 */
export function textbloecke(wurzel: Document | Element): TextBlock[] {
  const start = startVon(wurzel);
  if (!start) return [];
  const kandidat = new Set<Element>();
  pruefen(start, kandidat);
  const els: Element[] = [];
  sammeln(start, kandidat, els);
  if (!els.length) return [];
  const nr = nummern(wurzel);
  return els.map((el) => ({
    el: el as HTMLElement,
    schluessel: { tag: el.localName, nr: nr.get(el) ?? -1 },
    text: domBlockText(el),
    knoten: domTextKnoten(el),
  }));
}

/** Das Element mit diesem Tag und dieser Nummer (wie getElementsByTagName, ohne Elemente der Anzeige). */
export function elementBei(wurzel: Document | Element, s: BlockSchluessel): Element | null {
  let z = 0;
  for (const e of Array.from(wurzel.getElementsByTagName(s.tag))) {
    if (istUi(e) || e.localName !== s.tag) continue;
    if (z++ === s.nr) return e;
  }
  return null;
}

/** Gegenstück zu elementBei: Tag und Nummer eines Elements. */
export function elementSchluessel(wurzel: Document | Element, el: Element): BlockSchluessel | null {
  let z = 0;
  for (const e of Array.from(wurzel.getElementsByTagName(el.localName))) {
    if (istUi(e) || e.localName !== el.localName) continue;
    if (e === el) return { tag: el.localName, nr: z };
    z++;
  }
  return null;
}

/** Wie blockFinden, mit schon berechneter Blockliste. */
export function blockAusListe(wurzel: Document | Element, bloecke: TextBlock[], s: BlockSchluessel, vorherText: string): TextBlock | null {
  const text = normalisieren(vorherText);
  const genau = bloecke.find((b) => b.schluessel.tag === s.tag && b.schluessel.nr === s.nr);
  if (genau && genau.text === text) return genau;
  if (!genau) {
    const e = elementBei(wurzel, s);
    if (e && !e.querySelector([...STRUKTUR].join(',')) && domBlockText(e) === text) {
      return { el: e as HTMLElement, schluessel: { ...s }, text, knoten: domTextKnoten(e) };
    }
  }
  const treffer = bloecke.filter((b) => b.schluessel.tag === s.tag && b.text === text);
  return treffer.length === 1 ? treffer[0] : null;
}

/** Block mit diesem Schlüssel und Text; sonst der einzige Block gleichen Tags mit diesem Text. */
export function blockFinden(wurzel: Document | Element, schluessel: BlockSchluessel, vorherText: string): TextBlock | null {
  return blockAusListe(wurzel, textbloecke(wurzel), schluessel, vorherText);
}

/** Der Block, in dem `knoten` liegt (z. B. für eine Textmarkierung). */
export function blockZuKnoten(wurzel: Document | Element, knoten: Node): TextBlock | null {
  return textbloecke(wurzel).find((b) => b.el === knoten || b.el.contains(knoten)) ?? null;
}

/** Anker für einen Kommentar an diesem Block (Text gekürzt nach Zeichen, nie mitten im Emoji). */
export function ankerFuer(block: TextBlock): Anker {
  return { tag: block.schluessel.tag, nr: block.schluessel.nr, text: Array.from(block.text).slice(0, ANKER_TEXT_MAX).join('') };
}

/** Passt der (evtl. gekürzte) Anker-Text zu einem Blocktext? */
export function ankerTextPasst(blockText: string, ankerText: string): boolean {
  return blockText === ankerText || (Array.from(ankerText).length >= ANKER_TEXT_MAX && blockText.startsWith(ankerText));
}

// ── Aufbau eines Blocks (für Bearbeiten und Vergleich) ───────────────────────

interface Aufbau { knoten: Text[]; plaetze: number[]; signatur: string }

/** Gestaltende Attribute — fürs Bearbeiten zählt auch eine neue Farbe oder Linkadresse als Änderung. */
const GESTALT_ATTRIBUTE = ['style', 'class', 'href', 'color', 'face', 'size'];

function gestalt(e: Element): string {
  let s = '';
  for (const a of GESTALT_ATTRIBUTE) {
    const w = e.getAttribute(a);
    if (w !== null) s += ` ${a}=${JSON.stringify(w)}`;
  }
  return s;
}

/**
 * Elemente und Kommentare als Folge; Texte (wie domTextKnoten) mit dem „Platz“
 * zwischen zwei Tags. `mitGestalt`: gestaltende Attribute gehören zur Signatur.
 */
function aufbau(el: Element, mitGestalt = false): Aufbau {
  const knoten: Text[] = [];
  const plaetze: number[] = [];
  let platz = 0;
  let signatur = '';
  const laufen = (e: Node) => {
    for (let k = e.firstChild; k; k = k.nextSibling) {
      if (k.nodeType === 3) { knoten.push(k as Text); plaetze.push(platz); continue; }
      platz++;
      if (k.nodeType === 8) { signatur += '<!>'; continue; }
      if (k.nodeType !== 1) continue;
      const name = (k as Element).localName;
      if (OHNE_TEXT.has(name)) { signatur += `<${name}/>`; continue; }
      signatur += mitGestalt ? `<${name}${gestalt(k as Element)}>` : `<${name}>`;
      laufen(k);
      signatur += `</${name}>`;
      platz++;
    }
  };
  laufen(el);
  return { knoten, plaetze, signatur };
}

/** Struktur eines Blocks ohne Texte (Tags und Kommentare in Reihenfolge; `mitGestalt`: samt Stil, Klasse, Linkadresse). */
export function struktursignatur(el: Element, mitGestalt = false): string {
  return aufbau(el, mitGestalt).signatur;
}

// ── Bearbeiten im iframe ────────────────────────────────────────────────────

/** Stand eines Blocks beim Start des Bearbeitens. */
export interface BlockStand {
  el: HTMLElement;
  schluessel: BlockSchluessel;
  text: string;
  knoten: string[];
  plaetze: number[];
  signatur: string;
}

const BEARBEITEN_STIL_ID = 'za-bearbeiten';
const BEARBEITEN_STIL = `
[data-za-block][contenteditable]{outline:1px dashed rgba(109,23,35,.28)!important;outline-offset:2px;border-radius:3px;cursor:text;transition:outline-color .15s ease}
[data-za-block][contenteditable]:hover{outline-color:rgba(109,23,35,.65)!important}
[data-za-block][contenteditable]:focus{outline:2px solid #6D1723!important;outline-offset:3px}
[data-za-block][contenteditable] a{cursor:text}
`;

const helfer = new WeakMap<Document, (ev: Event) => void>();

/** Ersatz, wenn der Browser insertLineBreak nicht kennt. */
function umbruchEinfuegen(doc: Document): void {
  const sel = doc.getSelection();
  if (!sel || !sel.rangeCount) return;
  const r = sel.getRangeAt(0);
  r.deleteContents();
  const br = doc.createElement('br');
  r.insertNode(br);
  r.setStartAfter(br);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
}

/** Enter = Zeilenumbruch (statt neuem <div>), Einfügen nur als Text, kein Ziehen. */
function eingabeHelfer(doc: Document): (ev: Event) => void {
  return (ev: Event) => {
    const ziel = ev.target as Element | null;
    if (!ziel || typeof ziel.closest !== 'function' || !ziel.closest('[data-za-block]')) return;
    if (ev.type === 'keydown') {
      const k = ev as KeyboardEvent;
      if (k.key === 'Enter' && !k.isComposing) {
        k.preventDefault();
        if (!doc.execCommand('insertLineBreak')) umbruchEinfuegen(doc);
      }
    } else if (ev.type === 'paste') {
      const daten = (ev as ClipboardEvent).clipboardData;
      ev.preventDefault();
      if (daten) doc.execCommand('insertText', false, daten.getData('text/plain'));
    } else if (ev.type === 'drop') ev.preventDefault();
  };
}

/**
 * Macht jeden Textblock einzeln bearbeitbar (contenteditable, data-za-block="tag:nr")
 * und merkt sich den Stand vorher. Platzhalter wie {{ contact.VORNAME }} bleiben
 * wörtlich stehen.
 */
export function editierbarMachen(doc: Document): BlockStand[] {
  const staende: BlockStand[] = textbloecke(doc).map((b) => {
    const a = aufbau(b.el, true);
    return { el: b.el, schluessel: b.schluessel, text: b.text, knoten: a.knoten.map((t) => t.data), plaetze: a.plaetze, signatur: a.signatur };
  });
  for (const s of staende) {
    s.el.setAttribute('contenteditable', 'true');
    s.el.setAttribute('data-za-block', `${s.schluessel.tag}:${s.schluessel.nr}`);
  }
  if (!doc.getElementById(BEARBEITEN_STIL_ID)) {
    const st = doc.createElement('style');
    st.id = BEARBEITEN_STIL_ID;
    st.textContent = BEARBEITEN_STIL;
    (doc.head ?? doc.documentElement).appendChild(st);
  }
  if (!helfer.has(doc)) {
    const h = eingabeHelfer(doc);
    helfer.set(doc, h);
    for (const t of ['keydown', 'paste', 'drop']) doc.addEventListener(t, h, true);
  }
  return staende;
}

/** Nimmt contenteditable, data-za-block, den Bearbeiten-Stil und die Eingabehilfen wieder heraus. */
export function editierbarEntfernen(doc: Document): void {
  doc.querySelectorAll('[data-za-block]').forEach((e) => {
    e.removeAttribute('contenteditable');
    e.removeAttribute('data-za-block');
  });
  doc.getElementById(BEARBEITEN_STIL_ID)?.remove();
  const h = helfer.get(doc);
  if (h) {
    for (const t of ['keydown', 'paste', 'drop']) doc.removeEventListener(t, h, true);
    helfer.delete(doc);
  }
}

function knotenZuordnen(s: BlockStand, jetzt: Aufbau): string[] | null {
  const proPlatz = new Map<number, string>();
  jetzt.knoten.forEach((t, k) => {
    const p = jetzt.plaetze[k];
    proPlatz.set(p, (proPlatz.get(p) ?? '') + t.data);
  });
  const belegt = new Set<number>();
  const out: string[] = [];
  for (const p of s.plaetze) {
    if (belegt.has(p)) return null;
    belegt.add(p);
    out.push(proPlatz.get(p) ?? '');
  }
  for (const [p, t] of proPlatz) if (!belegt.has(p) && t !== '') return null; // Text an neuer Stelle
  return out;
}

/**
 * Vergleicht den aktuellen Inhalt der Blöcke mit dem Stand aus
 * editierbarMachen() und liefert je geändertem Block eine Aenderung.
 * Gleiche Struktur → nachher_knoten; sonst bereinigtes nachher_html.
 */
export function aenderungenErmitteln(staende: BlockStand[]): Aenderung[] {
  const out: Aenderung[] = [];
  for (const s of staende) {
    if (!s.el.isConnected) continue;
    const jetzt = aufbau(s.el, true);
    const text = domBlockText(s.el);
    if (text === s.text && jetzt.signatur === s.signatur) continue;
    const nachher = jetzt.signatur === s.signatur ? knotenZuordnen(s, jetzt) : null;
    const nachherHtml = nachher ? null : bereinigen(s.el.innerHTML);
    // Bei neuem HTML zählt dessen Text (Bereinigen kann Umbrüche setzen oder Teile entfernen)
    let nachherText = text;
    if (nachherHtml !== null) {
      const tpl = document.createElement('template');
      tpl.innerHTML = nachherHtml;
      nachherText = domBlockText(tpl.content);
    }
    out.push({
      block: { tag: s.schluessel.tag, nr: s.schluessel.nr },
      vorher_text: s.text,
      nachher_text: nachherText,
      vorher_knoten: [...s.knoten],
      nachher_knoten: nachher,
      nachher_html: nachherHtml,
    });
  }
  return out;
}
